<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div v-if="anomaliesList.length > 0" class="wy-analytics-contact-reasons-detail-topic-anomalies">
    <div class="section-row-1">
      <div class="anomalies-event-section">
        <div class="div-anomalies-event-header">Event</div>
        <el-table
          :data="anomaliesList"
          ref="anomaliesEventTable"
          border
          stripe
          class="anomalies-event-table"
          highlight-current-row
          @cell-click="handleCellClick"
          style="min-width: 500; width: 100%;">
          <el-table-column
            prop="event"
            label=""
            class-name="event-name"
            width="150">
              <template slot-scope="scope">
                <div :class="`dot-${scope.row.event}`"></div>
                <span style="margin-left: 10px">{{ scope.row.eventName }}</span>
              </template>
          </el-table-column>
          <el-table-column
            prop="content"
            label="">
          </el-table-column>
          <div slot="empty">
            <div class="no-data-title">No anomalies found</div>
            <div class="no-data"></div>
          </div>
        </el-table>
      </div>
      <div class="anomalies-chart-section">
        <detailAnomaliesChart :activeTab="activeTab" :chartData="chartData" />        
      </div>
    </div>

    <div class="section-row-2" v-if="!transcriptList || transcriptList.length > 0">
      <div class="anomalies-transcript-viewer-section">
        <div class="div-anomalies-transcript-viewer-header">
          <div class="div-header-title">Transcript Viewer</div>
          <div class="div-transcript-search">
            <el-input v-model="inputAnomaliesTranscriptSearch" suffix-icon="el-icon-click" @keyup.enter.native="handleSearch">
            </el-input>
          </div>
        </div>
        <div class="div-anomalies-transcript-viewer-content">
          <div class="page-content" v-for="(count) in displayPageCount" :key="count" 
            v-loading="loadingTranscript"
            element-loading-text=""
            element-loading-spinner="el-icon-loading"
            element-loading-background="#FFFFFF">
            <div class="div-transcript-header">
              <div v-if="transcriptList && transcriptList[(currentPage-1)*step+count-1] && transcriptList[(currentPage-1)*step+count-1].conversationId" class="transcripts-viewer-header-left">
                <div class="transcripts-viewer-date-header">{{formatMessageHeaderDate(transcriptList[(currentPage-1)*step+count-1].date)}}</div>
                <div class="transcripts-viewer-time-header">{{getTimeRange(transcriptList[(currentPage-1)*step+count-1])}}</div>
              </div>
              <div class="info-mark">
                <el-popover
                  v-if="transcriptList && transcriptList.length>0 && (currentPage-1)*step+count<=totalPages"
                  placement="top"
                  title=""
                  width="280"
                  popper-class="contact-reason-transcript-info-pop-over"
                  trigger="hover">
                  <div>
                    <div>Conversation ID: {{transcriptList[(currentPage-1)*step+count-1].conversationId}}</div>
                    <!-- <div>Date: {{formatMessageDate(transcriptList[(currentPage-1)*step+count-1].date)}}</div> -->
                    <!-- <div>Time: {{getTimeRange(transcriptList[(currentPage-1)*step+count-1])}}</div> -->
                  </div>
                  <el-button v-if="(currentPage-1)*step+count<=totalPages" slot="reference" class="el-infor-icon"></el-button>
                </el-popover>
              </div>
            </div>
            <div class="div-transcript-content" v-if="transcriptList && transcriptList.length>0">
              <div v-if="(currentPage-1)*step+count<=totalPages">
                <div 
                  v-for=" (transcript, index) in transcriptList[(currentPage-1)*step+count-1].messages" 
                  :key="index" 
                  class="div-transcript-row">
                    <div class="div-sent-by" v-if="(currentPage-1)*step+count-1<=totalPages">{{toTranscriptLabel(transcript.sentBy)}}</div>
                    <div class="div-transcript-text" v-if="(currentPage-1)*step+count-1<=totalPages">{{transcript.text}}</div>
                </div>
              </div>
              <div v-else>
              </div>
            </div>
          </div>
        </div>
        <div v-if="transcriptList && transcriptList.length>0" class="div-anomalies-transcript-viewer-footer">
          <div v-if="(currentPage-1)*step+4<=totalPages" class="div-page-infor">Showing {{(currentPage-1)*step+1}}-{{(currentPage-1)*step+4}} of {{totalPages}}</div>
          <div v-else class="div-page-infor">Showing {{(currentPage-1)*step+1}}-{{totalPages}} of {{totalPages}}</div>
          <div class="div-pagination">
            <el-pagination
              ref="pagination"
              :page-size="20"
              :page-count="totalPages"
              :current-change="currentPage"
              prev-text="< Previous"
              next-text="Next >"
              @current-change="pageChanged"
              layout="prev, pager, next">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="no-topic-content">
      <div class="no-data"></div>
        <div class="no-data-content">
          <div class="no-data-title">Sorry! You don't have any transcripts saved.</div>
          <div class="no-data-subtitle">To save a transcript, click the star icon in the top right corner of a transcript.</div>
        </div>   
    </div>
  </div>
  <div class="no-anomalies-content" v-else>
    <div class="no-data"></div>
    <div class="no-data-title">No anomalies found</div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import detailAnomaliesChart from './wy-analytics-contact-reasons-detail-topic-anomalies-chart';

export default Vue.extend({
  name: 'wy-analytics-contact-reasons-detail-topic-anomalies',
  props: ['parent', 'activeTab'],

  components: {
    'detailAnomaliesChart': detailAnomaliesChart,
  },

  data() {
    return {
      module: this,
      loadingTranscript: false,
      initDate: null,
      eventTypes: {
        'volume': {column: 'sumVolume', chartColumn: 'sumVolume', title: 'Volume', format: ',f', suffix: ''},
        'avgHandleTime': {column: 'avgHandlingTime', chartColumn: 'sumHandlingTime', title: 'Handling Time', format: ',f', suffix: 'm'}        
      },

      anomaliesList: [],
      topics: null,

      transcriptList: null,

      displayPageCount: [1, 2, 3, 4],
      currentPage: 1,
      totalPages: 0,
      step:4,

      tableData1: null,
      inputAnomaliesTranscriptSearch: '',
      transcriptDate: null,

      topicData: null,
      chartData: {}
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'tableModel', 'isHideResponse']),

  },

  methods: {
    ...mapActions(['fetchContactReasonsTopicAnomalies', 'fetchContactReasonsTopicConversations']),

    startLoading() {
      this.initDate = new Date();
      if(!this.loading) {
        this.logEventLoaded();
      }
    },

    logEventLoaded() {
      if(this.initDate) {
        this.$store.dispatch('amplitudeLogWithFilters', {name: 'anomalies_loaded', data: {topic_name: this.topicData.topicDisplayName, data_load_time: WyUtils.getTimeDiff(this.initDate)}});
        this.initDate = null;
      }
    },
    
    toTranscriptLabel(val) {
      return WyUtils.toTranscriptLabel(val);
    },
    
    async handleSearch(){
      if(this.transcriptDate!=null){
        this.loadingTranscript = true;
        let result = await this.fetchContactReasonsTopicConversations({from: 'anomalies', topics: this.topicData.mergedTopics, date: this.transcriptDate, textQuery: this.inputAnomaliesTranscriptSearch});
        if(result && result.data) {
          this.transcriptList = result.data;
          this.totalPages = result.data.length;
          this.currentPage = 1;
          this.pageChanged(1)
          if(document.querySelector(".div-anomalies-transcript-viewer-footer button.btn-prev")){
            document.querySelector(".div-anomalies-transcript-viewer-footer button.btn-prev").disabled = true
          }
          if(this.$refs.pagination && this.$refs.pagination.internalCurrentPage){
            this.$refs.pagination.internalCurrentPage = 1;
          }
        }
        this.loadingTranscript = false;
      }
    },

    async handleCellClick(row){
      if(row && row.date){
        this.loadingTranscript = true;
        this.transcriptDate = row.date;
        this.prepareChartData(row);

        let sortBy = 'date';
        if(row.event == 'avgHandleTime') {
          sortBy = 'handleTime'
        }
        if(row.event == 'avgResponseTime') {
          sortBy = 'responseTime'
        }
        let result = await this.fetchContactReasonsTopicConversations({from: 'anomalies', topics: this.topicData.mergedTopics, date: row.date, sortBy: sortBy})
        if(result && result.data) {
          this.transcriptList = result.data;
          this.totalPages = result.data.length;
          this.currentPage = 1;
          this.pageChanged(1)
          if(document.querySelector(".div-anomalies-transcript-viewer-footer button.btn-prev")){
            document.querySelector(".div-anomalies-transcript-viewer-footer button.btn-prev").disabled = true
          }
          if(this.$refs.pagination && this.$refs.pagination.internalCurrentPage){
            this.$refs.pagination.internalCurrentPage = 1;
          }
        }
        this.loadingTranscript = false;                
      }

      this.parent.stopLoadingByTab('anomalies');
    },

    pageChanged: function(pageNo) {
      if(this.currentPage != pageNo) {
        this.currentPage = pageNo;
      }
      if(document.querySelector(".div-anomalies-transcript-viewer-footer button.btn-next")) {
        if(pageNo * this.step >= this.totalPages){
          document.querySelector(".div-anomalies-transcript-viewer-footer button.btn-next").disabled = true
        }
        else{
          document.querySelector(".div-anomalies-transcript-viewer-footer button.btn-next").disabled = false
        }
      }
    },

    formatMessageDate(date) {
      return WyUtils.formatDate(date);
    },

    formatMessageHeaderDate(date) {
      return WyUtils.formatDateWithDay(date);
    },

    getTimeRange(transcript) {
      if(transcript.messages && transcript.messages.length > 1) {
        let startTime = moment(transcript.messages[0].datetime).format("h:mm a");
        let endTime = moment(transcript.messages[transcript.messages.length-1].datetime).format("h:mm a");
        return startTime + ' - ' + endTime;
      }
      else if(transcript.messages && transcript.messages.length  == 1) {
        return moment(transcript.messages[0].datetime).format("h:mm a");
      }
      else {
        return ''
      }
    },

    prepareChartData(row) {
      let eventType = this.eventTypes[row.event];
      if(eventType && this.topicData) {
        let trendList = this.topicData.trend;
        
        if(trendList && Array.isArray(trendList) && trendList.length > 0) {
          this.chartData =  {
            id: row.event,
            title: eventType.title,
            column: eventType.chartColumn,
            data: this.topicData,
            format: eventType.format,
            suffix: eventType.suffix,
            transcriptDate: row.date
          }

          trendList.forEach(trendItem => {
            let anomalyItem = this.anomaliesList.find(el => (el.event==row.event && el.date==trendItem.trendDate));
            if(anomalyItem) {
              trendItem.anomaly = anomalyItem.value;
            }
            else {
              trendItem.anomaly = 0;
            }
          });
        }
      }
    },

    async loadTopicAnomalies() {
      if(this.parent.currentItem) {
        this.topicData = this.parent.currentItem;
        if(this.topicData) {
          let result = await this.fetchContactReasonsTopicAnomalies({topics: this.topicData.mergedTopics, topicDisplayName: this.topicData.topicDisplayName});
          this.anomaliesList = [];
          if(result.data && result.data.length > 0) {
            result.data.forEach(anomaly => {
              let formatedDate = moment(anomaly.date).format("ddd MMM DD, YYYY");
              
              let eventType = this.eventTypes[anomaly.anomalyMetric];
              if(eventType) {
                let formatedContent = '';
                if(eventType.suffix == 'm') {
                  formatedContent = `${formatedDate}, ${eventType.title} was ${WyUtils.toNiceTimeFormatFromMinus(anomaly.anomalyValue)}`;
                }
                else {
                  formatedContent = `${formatedDate}, ${eventType.title} was ${anomaly.anomalyValue}`;
                }

                let obj = {};
                obj.event = anomaly.anomalyMetric;
                obj.eventName = eventType.title;
                obj.content = formatedContent;
                obj.date = anomaly.date;
                obj.dot = anomaly.anomalyMetric
                obj.value = anomaly.anomalyValue
                this.anomaliesList.push(obj);
              }
            });

            setTimeout(()=>{
              if(this.anomaliesList.length > 0 && this.$refs.anomaliesEventTable && this.$refs.anomaliesEventTable.setCurrentRow){
                this.$refs.anomaliesEventTable.setCurrentRow(this.anomaliesList[0]);                
                this.handleCellClick(this.anomaliesList[0]);
              }
              else {
                this.parent.stopLoadingByTab('anomalies');
              }
            }, 300)
          }
          else {
            this.parent.stopLoadingByTab('anomalies');
          }
        }
        else {
          this.parent.stopLoadingByTab('anomalies');
        }
      }
      else {
        this.parent.stopLoadingByTab('anomalies');
      }
    }
  },

  async created() {
    if(!this.isHideResponse) {
      this.eventTypes.avgResponseTime = {column: 'avgResponseTime', chartColumn: 'sumResponseTime', title: 'Response Time', format: ',f', suffix: 'm'}
    }
    this.loadTopicAnomalies();
  },
});
</script>

<style lang="scss">
.wy-analytics-contact-reasons-detail-topic-anomalies {
  color: var(--site-font-primary-color);  
  .section-row-1 {
    display: inline-flex;
    //margin: 5px 0px 0px 25px;
    width: 100%;
    border-bottom: 1px #E8E8E8 solid;

    .anomalies-event-section {
      width: 50%;

      .div-anomalies-event-header {
        padding: 20px 0 10px 0px;
        font-weight: 500;
      }
      .el-table {
        background-color: transparent !important;
      }
      .el-table--border {
        border: 0px;
      }
      .el-table::before{
        height: 0px;
      }
      .el-table .cell {
        line-height: 15px;
      }
      .el-table__header-wrapper {
        display: none
      }
      .el-table__body-wrapper {
        overflow-x: hidden;
        overflow-y: auto;
        height: 240px;
        color: var(--site-font-primary-color);
        font-size: 12px;
        border: 0px;

        .el-table__body {
          tr.el-table__row {
            background-color: #ffffff;
            cursor: pointer;
          }
          
          tr.el-table__row--striped td{
            background-color: #ffffff;
          }
          tr.el-table__row td {
            border-right: 0px;
          }
          .el-table__row--striped td:hover{
            background-color: #FAFDFF;
          }
          td:hover{
            background-color: #FAFDFF;
          }
          tr:hover>td {
            background-color: transparent;
          }
          .current-row td {
            background-color: #E2ECF7 !important;
          }
          tbody .event-name {
            font-weight: 500;
          }
        }
      }
      
      .dot-volume {
        border-radius: 10px;
        height: 10px;
        width: 10px;
        display: inline-block;
        background-color: #B364C6;
      }
      .dot-avgHandleTime {
        border-radius: 10px;
        height: 10px;
        width: 10px;
        display: inline-block;
        background-color: #455AC2;
      }
      .dot-avgResponseTime {
        border-radius: 10px;
        height: 10px;
        width: 10px;
        display: inline-block;
        background-color: #53AAFF;
      }
      .dot-countPositive {
        border-radius: 10px;
        height: 10px;
        width: 10px;
        display: inline-block;
        background-color: #10A29B;
      }
      .dot-countNegative {
        border-radius: 10px;
        height: 10px;
        width: 10px;
        display: inline-block;
        background-color: #C24570;
      }
      .dot-countResolved {
        border-radius: 10px;
        height: 10px;
        width: 10px;
        display: inline-block;
        background-color: #047204;
      }
    }
    
    .anomalies-chart-section {
      width: 50%;
      min-width: 545px;
      margin-left: 20px;
      border: 0px solid red;
    }
  }
  .section-row-2 {
    display: block;
    //margin: 12px 0px 2px 25px;
    margin-top: 10px;

    ul.el-pager {
      display: none;
    }

    .div-anomalies-transcript-viewer-header {
      padding: 13px 5px;
      font-weight: 500;
      display: inline-flex;
      width: -webkit-fill-available;
      padding-right: 25px;

      .div-header-title {
        width: 100%;
      }
      .div-transcript-search {
        width: 300px;
        float: right;
        background: #FFFFFF 0% 0% no-repeat padding-box;
        box-shadow: 0px 3px 6px #00000029;
        border-radius: 19px;

        input {
          border-radius: 19px;
          height: 30px;
          padding-left: 17px;
        }
        .el-input__icon.el-icon-click {
          background-image: url('../../assets/icons/icon_search.svg');
          background-repeat: no-repeat;
          background-size: 18px 18px;
          background-position-y: 5px;
          background-position-x: 0px;
        }
      }
    }

    .div-anomalies-transcript-viewer-content {
      display: inline-flex;
      width: 100%;
      .el-table__body-wrapper {
        overflow-x: hidden;
        overflow-y: auto;
        height: 240px;
      }
      .page-content {
        border: 0.5px solid var(--unnamed-color-cbcbcb);
        background: #FFFFFF 0% 0% no-repeat padding-box;
        border: 0.5px solid #CBCBCB;
        border-radius: 10px;
        min-height: 378px !important;
        min-width: 257px;
        width:25%;
        margin: 0px 15px 0px 0px;
        height: calc(100vh - 655px);
        // overflow-y: auto;
        // overflow-x: hidden;

        .div-transcript-header {
          height: 36px;
          border-bottom: 1px #CBCBCB solid;
          display: inline-block;
          min-width: 257px;
          width: 100%;

          .transcripts-viewer-header-left {
            float: left;
            width: 80%;
          }

          .transcripts-viewer-date-header {
            display: inline-block;
            font-size: 14.22px;
            font-weight: 400;
            padding-left: 10px;
            padding-top: 12px;
          }
          .transcripts-viewer-time-header {
            float: right;
            font-size: 14.22px;
            font-weight: 400;
            padding-left: 0px;
            // padding-top: 13px;
          }
          .el-infor-icon {
            cursor: pointer;
            background-image: url('../../assets/icons/icon_info.svg');
            background-size: 80%;
            background-repeat: no-repeat;
            background-position-y: 1px;
            background-position-x: 4px;
            height: 28px;
            border-radius: 100px;
            border: 0px;
            margin: 6px 13px 0 0;
            padding: 12px 14px 14px 14px;
            float: right;
          }
          .el-button:focus {
            background-color: unset
          }
          .el-button:hover {
            background-color: unset;
          }
        }
        .div-transcript-content {
          min-height: 326px !important;
          height: calc(100vh - 712px);  
          padding: 3px 0px;
          overflow-y: auto;
          overflow-x: hidden;
          min-width: 257px;
          font-size: 12px;

          &::-webkit-scrollbar {
            width: 20px;
          }
          &::-webkit-scrollbar-thumb {
            border-radius: 160px;
            border: 2px solid transparent;
            border-top: 4px solid transparent;
            background-clip: content-box;
            background-color: #CAD2DA;
          }
          &::-webkit-scrollbar-track {
            background:#fff;
            border-top: 1px solid #CAD2DA;
          }
          &::-webkit-scrollbar:vertical {
            width: 14px;
          }
          &::-webkit-scrollbar-thumb:vertical {
            border-radius: 160px;
            border: 2px solid transparent;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            background-clip: content-box;
            background-color: #CAD2DA;
          }
          &::-webkit-scrollbar-track:vertical {
            //border-left: 1px solid #CAD2DA;
            border-top: none !important;
          }

          .div-transcript-row {
            display: inline-flex;
            padding: 5px 15px 5px 11px;
            border-bottom: 1px #CBCBCB solid;
            min-width: 245px;
            width: 100%;

            .div-sent-by {
              font-weight: 500;
              padding-right: 10px;
              min-width: 33px;
            }
            .div-transcript-text {
              word-break: break-word;
              // max-width: 178px;
              width: 80%;
              padding-right: 11px;
            }
          }
        }
      }

      .el-loading-mask {
        height: unset !important;
        opacity: 0.9;
        border-radius: 10px;

        .el-loading-spinner .el-loading-text {
          display: none;
        }
        .el-loading-spinner {
          top: 35%;
          background: var(--site-loading-icon);
          background-repeat: no-repeat;
          background-size: 25px;
          background-position: center;    
          .circular {
            display: inline-block;
            width: 90px;
            height: 90px;
          }
        }
        .el-loading-spinner .path {
          -webkit-animation: loading-dash 2s ease-in-out infinite;
          animation: loading-dash 2s ease-in-out infinite;
          stroke-dasharray: 90,150;
          stroke-dashoffset: 0;
          stroke-width: 2.5 !important;
          stroke: #0C2837 !important;
          stroke-linecap: round;
          
        }
        .el-loading-spinner i {
          display: none;
        }
      }
    }
    .div-anomalies-transcript-viewer-footer {
      display: inline-block;
      width: 100%;

      .div-page-infor {
        float: left;
        padding-left: 10px;
        padding-top: 13px;
        font-size: 12px;
        line-height: 28px;
        color: #ADADAD;
      }
      .div-pagination {
        float: right;
        padding-right: 25px;
        padding-top: 10px;
        
        .el-pagination {
          padding-right: 0px;
        }
        .el-pagination button {
          color: #214586;
          padding-right: 0px;
          padding-left: 30px;
          background-color: transparent;
        }
        .el-pagination button:disabled {
          color: #C0C4CC;
        }
        .el-pagination span:not([class*=suffix]) {
          word-spacing: 7px;
        }
      }
    }
  }
  .anomalies-event-table {
    .has-gutter {
      display: none;
    }
    .el-table__empty-block {
      height: 180px !important;
      width: 100% !important;
      .no-data {
        text-align: center;
        display: block;
        border: none;
        height: 83px;
        // width: 100%;
        margin-top: 0px;
        background-position-x: 50%;
        background-repeat: no-repeat;
        background-size: 30%;
        background-image: url('../../assets/icons/icon_no_anomalies.svg');
      }
      .no-data-title {
        width: 100%;
        // margin-left: 11%;
        margin-top: 0px;
        text-align: center;
        font-size: 13px;
        color: var(--site-font-primary-color) !important;
        font-weight: 500;
      }
      .no-data-content-first-row {
        width: 160px;
        line-height: 20px;
        color: var(--site-font-primary-color) !important;
      }
      .no-data-content-second-row {
        width: 160px;
        line-height: 20px;
        padding-top: 13px;
        color: var(--site-font-primary-color) !important;
      }
    }
  }
}
.no-anomalies-content{
  color: var(--site-font-primary-color);
  background-color: #FFFF;
  margin-bottom: 3px;
  height: calc(100vh - 356px);

  .no-data {
    text-align: center;
    display: block;
    border: none;
    // height: 130px;
    height: 184px;
    margin-top: 100px;
    background-position-x: 50%;
    background-repeat: no-repeat;
    background-size: 138px;
    background-image: url('../../assets/icons/icon_no_anomalies.svg');
  }
  .no-data-title {
    width: 100%;
    margin-top: 0px;
    text-align: center;
    font-size: 20px;
    color: var(--site-font-primary-color) !important;
    font-weight: 600;
  }
  .no-data-content-first-row {
    width: 160px;
    line-height: 20px;
    color: var(--site-font-primary-color) !important;
  }
  .no-data-content-second-row {
    width: 160px;
    line-height: 20px;
    padding-top: 13px;
    color: var(--site-font-primary-color) !important;
  }
}
.contact-reason-transcript-info-pop-over {
  font-size: 13px;
  width: fit-content !important;
  padding: 12px 15px 10px 15px;
  box-shadow: 0px 0px 20px #0000002E;
  border-radius: 10px;
  .popper__arrow {
    display: block !important;
  }
}
.no-data-content {
  width: 100%;
  margin-top: 0px;
  text-align: center;
  color: var(--site-font-secondary-color) !important;
  .no-data-subtitle {
    margin-top: 24px;
    font-size: 14px;
    color: var(--site-font-secondary-color) !important;
    font-weight: 400;
  }
}
</style>